/**
 * AcessoEfetivoCard — RBAC v4 Fase 1 (TASK-2548).
 *
 * Esclarece pro usuario a separacao entre:
 *  - Roles (labels legacy — afetam dashboard padrao, audit notifications,
 *    JWT do broker ZTNA) — exibidas pelo IdentidadeCard como badges.
 *  - Template aplicado + permissoes efetivas (RBAC v3 — fonte real das
 *    capacidades de acesso a recursos) — exibidas neste card.
 *
 * Spec do gap: docs/rbac-v4/audit-is-admin-2026-05-18.md.
 */
import {
  Stack, Group, Text, Badge, Box, Collapse, Button, Skeleton, Code,
  SimpleGrid, ThemeIcon,
} from '@mantine/core'
import {
  IconShieldCheck, IconShieldLock, IconKey, IconUserStar,
  IconChevronDown, IconChevronUp,
} from '@tabler/icons-react'
import { useState, useMemo } from 'react'
import { useAuthStore } from '@/store/auth'
import { WidgetCard } from '@/components/ui'

export function AcessoEfetivoCard() {
  const { user } = useAuthStore()
  const [showPerms, setShowPerms] = useState(false)

  const allPerms = useMemo(() => {
    if (!user) return [] as string[]
    const set = new Set<string>(user.global_permissions ?? [])
    for (const perms of Object.values(user.tenant_permissions ?? {})) {
      for (const p of perms) set.add(p)
    }
    return Array.from(set).sort()
  }, [user])

  if (!user) {
    return (
      <WidgetCard p="md">
        <Skeleton h={180} />
      </WidgetCard>
    )
  }

  // Backward-compat: campos opcionais ate refetch automatico apos deploy.
  const templateName = user.applied_template_name ?? null
  const tplPermCount = user.applied_template_perm_count ?? 0
  const effectivePerms = user.effective_perm_count ?? allPerms.length
  const isGlobalAdmin = !!user.is_admin

  return (
    <WidgetCard p="md">
      <Stack gap="md">
        <Group gap="xs" align="center">
          <ThemeIcon size="md" radius="md" variant="light" color="inovareTeal">
            <IconShieldCheck size={18} />
          </ThemeIcon>
          <Text size="md" fw={600}>Acesso efetivo</Text>
          <Badge variant="light" color="gray" size="xs">
            o que você pode fazer
          </Badge>
        </Group>

        <Text size="xs" c="dimmed">
          Suas permissões reais são calculadas a partir do <strong>template aplicado</strong>{' '}
          + permissões por tenant. Os <em>perfis</em> mostrados acima (Administrador,
          DevOps, etc.) são labels que afetam categorizacao, dashboard padrão e
          notificações de auditoria, mas não definem o acesso a recursos.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <Box>
            <Group gap="xs" mb={4}>
              <IconKey size={14} stroke={1.5} />
              <Text size="xs" c="dimmed" fw={500}>Perfil aplicado</Text>
            </Group>
            {templateName ? (
              <Badge variant="filled" color="inovareTeal" size="md">
                {templateName}
              </Badge>
            ) : (
              <Text size="sm" c="dimmed" fs="italic">Nenhum template aplicado</Text>
            )}
            {tplPermCount > 0 && (
              <Text size="xs" c="dimmed" mt={4}>
                {tplPermCount} permissões via template
              </Text>
            )}
          </Box>

          <Box>
            <Group gap="xs" mb={4}>
              <IconShieldLock size={14} stroke={1.5} />
              <Text size="xs" c="dimmed" fw={500}>Permissões efetivas</Text>
            </Group>
            <Text size="xl" fw={700}>{effectivePerms}</Text>
            <Text size="xs" c="dimmed">
              soma única de template + UTP + bootstrap
            </Text>
          </Box>

          <Box>
            <Group gap="xs" mb={4}>
              <IconUserStar size={14} stroke={1.5} />
              <Text size="xs" c="dimmed" fw={500}>Status</Text>
            </Group>
            {isGlobalAdmin ? (
              <Badge variant="light" color="grape" size="md">
                Administrador global
              </Badge>
            ) : (
              <Badge variant="light" color="gray" size="md">
                Standard
              </Badge>
            )}
            {isGlobalAdmin && (
              <Text size="xs" c="dimmed" mt={4}>
                acesso a todos os tenants ativos
              </Text>
            )}
          </Box>
        </SimpleGrid>

        <Box>
          <Button
            variant="subtle"
            size="xs"
            rightSection={
              showPerms ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />
            }
            onClick={() => setShowPerms((s) => !s)}
          >
            {showPerms ? 'Ocultar' : 'Ver'} lista completa de permissões ({allPerms.length})
          </Button>
          <Collapse in={showPerms}>
            <Group gap={4} mt="sm" wrap="wrap">
              {allPerms.length === 0 && (
                <Text size="xs" c="dimmed" fs="italic">
                  Nenhuma permissão listada — provavelmente sem template e sem grants UTP.
                </Text>
              )}
              {allPerms.map((p) => (
                <Code key={p} c="inovareTeal">{p}</Code>
              ))}
            </Group>
          </Collapse>
        </Box>
      </Stack>
    </WidgetCard>
  )
}
